// frontend/src/pages/AdminDashboard.js
import React, { useContext, useState, useEffect } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import { Button } from 'react-bootstrap';
import AdminCustomerServiceModal from '../components/AdminCustomerServiceModal';
import api from '../api';

const AdminDashboard = () => {
  const { user, logout } = useContext(AuthContext);
  const [showCSModal, setShowCSModal] = React.useState(false);
  const [broadcastMsg, setBroadcastMsg] = useState('');
  const [broadcastLoading, setBroadcastLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth <= 768) {
        setSidebarCollapsed(true);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleBroadcast = async () => {
    if (!broadcastMsg.trim()) return;
    setBroadcastLoading(true);
    try {
      await api.post('/api/admin/broadcast', { message: broadcastMsg.trim() });
      setBroadcastMsg('');
      alert('广播已发送');
    } catch (e) {
      alert(e.response?.data?.message || '广播失败');
    }
    setBroadcastLoading(false);
  };

  const menuItems = [
    { path: '/admin/users', label: '用户管理', icon: '👥' },
    { path: '/admin/devices', label: '设备管理', icon: '🎮' },
    { path: '/admin/statistics', label: '数据统计', icon: '📊' },
    { path: '/admin/monitoring', label: '系统监控', icon: '📈' },
    { path: '/admin/partitions', label: '分区管理', icon: '🏢' },
    { path: '/admin/announcements', label: '公告管理', icon: '📢' },
    { path: '/admin/settings', label: '系统设置', icon: '⚙️' },
    { path: '/admin/reward-policy', label: '银币派奖策略', icon: '🏆' },
    { path: '/admin/sanqi-errors', label: '三七机异常处理', icon: '⚠️' },
    { path: '/admin/logs', label: '日志管理', icon: '📝' },
    { path: '/admin/withdrawals', label: '提现管理', icon: '💰' }
  ];

  // 外部监控链接
  const externalLinks = [
    { 
      url: 'http://localhost:9090', 
      label: 'Prometheus', 
      icon: '🔥',
      description: '指标监控'
    },
    { 
      url: 'http://localhost:3001', 
      label: 'Grafana', 
      icon: '📈',
      description: '可视化仪表盘'
    }
  ];

  return (
    <>
      <style>{adminDashboardStyles}</style>
      <div className="admin-dashboard">
        {/* 移动端菜单遮罩 */}
        {isMobile && !sidebarCollapsed && (
          <div 
            className="mobile-overlay"
            onClick={() => setSidebarCollapsed(true)}
          />
        )}

        {/* 侧边栏 */}
        <div className={`admin-sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
          <div className="sidebar-header">
            <div className="logo-section">
              <div className="logo">🎯</div>
              <h3 className="dashboard-title">管理员后台</h3>
            </div>
            {isMobile && (
              <button 
                className="close-btn"
                onClick={() => setSidebarCollapsed(true)}
              >
                ✕
              </button>
            )}
          </div>

          <div className="user-info">
            <div className="avatar">👤</div>
            <div className="user-details">
              <p className="username">欢迎, {user?.username || 'Admin'}!</p>
              <span className="user-role">系统管理员</span>
            </div>
          </div>

          <nav className="nav-menu">
            {menuItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) => 
                  `nav-link ${isActive ? 'active' : ''}`
                }
                onClick={() => isMobile && setSidebarCollapsed(true)}
              >
                <span className="nav-icon">{item.icon}</span>
                <span className="nav-label">{item.label}</span>
              </NavLink>
            ))}
          </nav>

          {/* 外部监控链接 */}
          <div className="external-links-section">
            <div className="external-links-header">
              <span className="external-links-title">外部监控</span>
            </div>
            {externalLinks.map((link) => (
              <button
                key={link.url}
                className="external-link"
                onClick={() => window.open(link.url, '_blank')}
                title={`${link.description} - ${link.url}`}
              >
                <span className="nav-icon">{link.icon}</span>
                <span className="nav-label">{link.label}</span>
              </button>
            ))}
          </div>

          <div className="sidebar-actions">
            <Button
              className="cs-btn"
              onClick={() => setShowCSModal(true)}
            >
              <span className="btn-icon">💬</span>
              <span className="btn-text">客服会话</span>
            </Button>

            <Button
              className="logout-btn"
              onClick={logout}
            >
              <span className="btn-icon">🚪</span>
              <span className="btn-text">退出登录</span>
            </Button>
          </div>
        </div>

        {/* 主内容区 */}
        <div className="admin-main">
          {/* 移动端菜单按钮 - 始终显示 */}
          {isMobile && (
            <button 
              className={`mobile-menu-btn ${!sidebarCollapsed ? 'menu-open' : ''}`}
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            >
              <div className="menu-icon">
                <span></span>
                <span></span>
                <span></span>
              </div>
              <span className="menu-text">
                {sidebarCollapsed ? '菜单' : '关闭'}
              </span>
            </button>
          )}

          {/* 广播功能区 */}
          <div className="broadcast-section">
            <div className="broadcast-header">
              <div className="broadcast-icon">📢</div>
              <h4>全服广播</h4>
            </div>
            <div className="broadcast-controls">
              <input
                type="text"
                value={broadcastMsg}
                onChange={e => setBroadcastMsg(e.target.value)}
                placeholder="输入全服广播内容..."
                className="broadcast-input"
                maxLength={200}
              />
              <button
                onClick={handleBroadcast}
                disabled={broadcastLoading || !broadcastMsg.trim()}
                className={`broadcast-btn ${broadcastLoading ? 'loading' : ''}`}
              >
                {broadcastLoading ? (
                  <>
                    <span className="loading-spinner"></span>
                    发送中...
                  </>
                ) : (
                  <>
                    <span className="btn-icon">📡</span>
                    发送广播
                  </>
                )}
              </button>
            </div>
            {broadcastMsg && (
              <div className="char-count">
                {broadcastMsg.length}/200
              </div>
            )}
          </div>

          {/* 子路由内容 */}
          <div className="main-content">
            <Outlet />
          </div>
        </div>

        <AdminCustomerServiceModal 
          show={showCSModal} 
          onHide={() => setShowCSModal(false)} 
        />
      </div>
    </>
  );
};

const adminDashboardStyles = `
  /* 基础样式重置 */
  .admin-dashboard * {
    box-sizing: border-box;
  }

  /* 主容器 */
  .admin-dashboard {
    display: flex;
    min-height: 100vh;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
    position: relative;
    overflow: hidden;
  }

  .admin-dashboard::before {
    content: '';
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 20% 80%, rgba(120, 119, 198, 0.3) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(255, 119, 198, 0.3) 0%, transparent 50%),
      radial-gradient(circle at 40% 40%, rgba(120, 219, 255, 0.2) 0%, transparent 50%);
    z-index: -1;
  }

  /* 移动端遮罩 */
  .mobile-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 998;
    backdrop-filter: blur(4px);
  }

  /* 侧边栏 */
  .admin-sidebar {
    width: 280px;
    background: rgba(255, 255, 255, 0.08);
    backdrop-filter: blur(25px);
    border-right: 1px solid rgba(255, 255, 255, 0.15);
    padding: 0;
    display: flex;
    flex-direction: column;
    position: relative;
    z-index: 999;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 
      4px 0 25px rgba(0, 0, 0, 0.15),
      0 0 50px rgba(0, 245, 255, 0.05);
  }

  .admin-sidebar::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      linear-gradient(180deg, 
        rgba(0, 245, 255, 0.02) 0%, 
        rgba(0, 102, 255, 0.02) 50%, 
        rgba(138, 43, 226, 0.02) 100%);
    z-index: -1;
  }

  .admin-sidebar.collapsed {
    transform: translateX(-100%);
  }

  /* 侧边栏头部 */
  .sidebar-header {
    padding: 24px 20px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .logo-section {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .logo {
    width: 40px;
    height: 40px;
    background: linear-gradient(135deg, #ff6b6b, #ee5a24);
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    box-shadow: 0 4px 12px rgba(238, 90, 36, 0.3);
  }

  .dashboard-title {
    color: white;
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    background: linear-gradient(135deg, #fff, #e1e8ed);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  .close-btn {
    background: none;
    border: none;
    color: white;
    font-size: 20px;
    cursor: pointer;
    padding: 4px;
    border-radius: 6px;
    transition: background-color 0.2s;
  }

  .close-btn:hover {
    background: rgba(255, 255, 255, 0.1);
  }

  /* 用户信息 */
  .user-info {
    padding: 20px;
    display: flex;
    align-items: center;
    gap: 15px;
    background: rgba(255, 255, 255, 0.08);
    margin: 0 16px 24px;
    border-radius: 18px;
    border: 1px solid rgba(255, 255, 255, 0.15);
    backdrop-filter: blur(15px);
    transition: all 0.3s ease;
    position: relative;
    overflow: hidden;
  }

  .user-info::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, 
      rgba(0, 245, 255, 0.05) 0%, 
      rgba(0, 102, 255, 0.03) 100%);
    z-index: -1;
  }

  .user-info:hover {
    transform: translateY(-2px);
    box-shadow: 
      0 8px 25px rgba(0, 0, 0, 0.15),
      0 0 20px rgba(0, 245, 255, 0.1);
    border-color: rgba(0, 245, 255, 0.3);
  }

  .avatar {
    width: 52px;
    height: 52px;
    background: linear-gradient(135deg, #00f5ff, #0066ff);
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 22px;
    box-shadow: 
      0 8px 20px rgba(0, 245, 255, 0.3),
      0 0 20px rgba(0, 245, 255, 0.2);
    border: 2px solid rgba(255, 255, 255, 0.2);
    transition: all 0.3s ease;
  }

  .user-info:hover .avatar {
    transform: scale(1.05);
    box-shadow: 
      0 12px 30px rgba(0, 245, 255, 0.4),
      0 0 30px rgba(0, 245, 255, 0.3);
  }

  .user-details {
    flex: 1;
  }

  .username {
    color: white;
    margin: 0 0 6px;
    font-size: 16px;
    font-weight: 600;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }

  .user-role {
    color: rgba(0, 245, 255, 0.8);
    font-size: 13px;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  /* 导航菜单 */
  .nav-menu {
    flex: 1;
    padding: 0 16px;
    overflow-y: auto;
  }

  .nav-link {
    display: flex;
    align-items: center;
    gap: 15px;
    padding: 16px 20px;
    margin: 6px 0;
    color: rgba(255, 255, 255, 0.9);
    text-decoration: none;
    border-radius: 16px;
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    overflow: hidden;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    backdrop-filter: blur(10px);
  }

  .nav-link::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.15), transparent);
    transition: left 0.6s;
  }

  .nav-link::after {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 4px;
    height: 0;
    background: linear-gradient(135deg, #00f5ff, #0066ff);
    border-radius: 2px;
    transition: height 0.3s ease;
  }

  .nav-link:hover {
    color: white;
    background: rgba(255, 255, 255, 0.15);
    transform: translateX(8px) scale(1.02);
    border-color: rgba(255, 255, 255, 0.3);
    box-shadow: 
      0 8px 25px rgba(0, 0, 0, 0.2),
      0 0 20px rgba(0, 245, 255, 0.1);
  }

  .nav-link:hover::before {
    left: 100%;
  }

  .nav-link:hover::after {
    height: 60%;
  }

  .nav-link.active {
    background: linear-gradient(135deg, 
      rgba(0, 245, 255, 0.2) 0%, 
      rgba(0, 102, 255, 0.15) 50%, 
      rgba(255, 255, 255, 0.1) 100%);
    color: white;
    border: 1px solid rgba(0, 245, 255, 0.4);
    box-shadow: 
      0 8px 32px rgba(0, 245, 255, 0.2),
      0 0 30px rgba(0, 245, 255, 0.1),
      inset 0 1px 0 rgba(255, 255, 255, 0.2);
    transform: translateX(6px);
    font-weight: 600;
  }

  .nav-link.active::after {
    height: 70%;
    background: linear-gradient(135deg, #00f5ff, #ffffff);
    box-shadow: 0 0 10px rgba(0, 245, 255, 0.5);
  }

  .nav-link.active .nav-icon {
    filter: drop-shadow(0 0 8px rgba(0, 245, 255, 0.6));
    color: #00f5ff;
  }

  .nav-link.active .nav-label {
    color: white;
    text-shadow: 0 0 10px rgba(0, 245, 255, 0.3);
  }

  .nav-icon {
    font-size: 20px;
    width: 28px;
    text-align: center;
    transition: all 0.3s ease;
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
  }

  .nav-label {
    font-weight: 600;
    font-size: 15px;
    letter-spacing: 0.3px;
    transition: all 0.3s ease;
  }

  /* 外部监控链接区域 */
  .external-links-section {
    padding: 0 16px;
    margin-top: 20px;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
    padding-top: 20px;
  }

  .external-links-header {
    padding: 12px 0;
    margin-bottom: 8px;
  }

  .external-links-title {
    color: rgba(255, 255, 255, 0.6);
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 1px;
  }

  .external-link {
    display: flex;
    align-items: center;
    gap: 15px;
    padding: 12px 20px;
    margin: 4px 0;
    color: rgba(255, 255, 255, 0.8);
    background: rgba(255, 255, 255, 0.03);
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    width: 100%;
    text-align: left;
    position: relative;
    overflow: hidden;
  }

  .external-link::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent);
    transition: left 0.6s;
  }

  .external-link:hover {
    color: white;
    background: rgba(255, 255, 255, 0.08);
    transform: translateX(4px) scale(1.01);
    border-color: rgba(255, 255, 255, 0.2);
    box-shadow: 
      0 4px 15px rgba(0, 0, 0, 0.15),
      0 0 15px rgba(0, 245, 255, 0.08);
  }

  .external-link:hover::before {
    left: 100%;
  }

  .external-link .nav-icon {
    font-size: 16px;
    width: 24px;
    text-align: center;
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.2));
  }

  .external-link .nav-label {
    font-size: 13px;
    font-weight: 500;
  }

  /* 侧边栏操作按钮 */
  .sidebar-actions {
    padding: 20px;
    border-top: 1px solid rgba(255, 255, 255, 0.15);
    display: flex;
    flex-direction: column;
    gap: 14px;
  }

  .cs-btn, .logout-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    padding: 14px 18px;
    border: none;
    border-radius: 14px;
    font-weight: 600;
    font-size: 14px;
    cursor: pointer;
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    text-decoration: none;
    width: 100%;
    position: relative;
    overflow: hidden;
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.1);
  }

  .cs-btn::before, .logout-btn::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent);
    transition: left 0.6s;
  }

  .cs-btn {
    background: linear-gradient(135deg, #00bcd4, #0097a7);
    color: white;
    box-shadow: 
      0 6px 20px rgba(0, 188, 212, 0.3),
      0 0 20px rgba(0, 188, 212, 0.1);
  }

  .cs-btn:hover {
    transform: translateY(-3px);
    box-shadow: 
      0 10px 30px rgba(0, 188, 212, 0.4),
      0 0 30px rgba(0, 188, 212, 0.2);
    background: linear-gradient(135deg, #26c6da, #00acc1);
  }

  .cs-btn:hover::before {
    left: 100%;
  }

  .logout-btn {
    background: linear-gradient(135deg, #f44336, #d32f2f);
    color: white;
    box-shadow: 
      0 6px 20px rgba(244, 67, 54, 0.3),
      0 0 20px rgba(244, 67, 54, 0.1);
  }

  .logout-btn:hover {
    transform: translateY(-3px);
    box-shadow: 
      0 10px 30px rgba(244, 67, 54, 0.4),
      0 0 30px rgba(244, 67, 54, 0.2);
    background: linear-gradient(135deg, #ef5350, #e53935);
  }

  .logout-btn:hover::before {
    left: 100%;
  }

  .logout-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(255, 107, 107, 0.4);
  }

  /* 主内容区 */
  .admin-main {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    position: relative;
  }

  /* 移动端菜单按钮 */
  .mobile-menu-btn {
    display: none;
    position: fixed;
    top: 20px;
    left: 20px;
    z-index: 1001;
    background: rgba(255, 255, 255, 0.15);
    backdrop-filter: blur(15px);
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-radius: 16px;
    padding: 12px 20px;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    gap: 10px;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 
      0 8px 25px rgba(0, 0, 0, 0.2),
      0 0 20px rgba(0, 245, 255, 0.1);
    min-width: 100px;
  }

  .mobile-menu-btn.menu-open {
    background: rgba(255, 69, 69, 0.15);
    border-color: rgba(255, 69, 69, 0.5);
    box-shadow: 
      0 8px 25px rgba(255, 69, 69, 0.3),
      0 0 20px rgba(255, 69, 69, 0.2);
  }

  .mobile-menu-btn.menu-open .menu-icon span {
    background: #ff4545;
  }

  .mobile-menu-btn.menu-open .menu-text {
    color: #ff4545;
  }

  .menu-icon {
    display: flex;
    flex-direction: column;
    gap: 3px;
    align-items: center;
    justify-content: center;
  }

  .menu-icon span {
    width: 20px;
    height: 2px;
    background: white;
    border-radius: 1px;
    transition: all 0.3s;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  }

  .menu-open .menu-icon span:nth-child(1) {
    transform: rotate(45deg) translate(5px, 5px);
  }

  .menu-open .menu-icon span:nth-child(2) {
    opacity: 0;
  }

  .menu-open .menu-icon span:nth-child(3) {
    transform: rotate(-45deg) translate(7px, -6px);
  }

  .menu-text {
    color: white;
    font-size: 14px;
    font-weight: 600;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
    letter-spacing: 0.5px;
    min-width: 35px;
    text-align: center;
  }

  .mobile-menu-btn:hover {
    background: rgba(255, 255, 255, 0.25);
    border-color: rgba(0, 245, 255, 0.5);
    transform: translateY(-2px) scale(1.05);
    box-shadow: 
      0 12px 35px rgba(0, 0, 0, 0.3),
      0 0 30px rgba(0, 245, 255, 0.2);
  }

  .mobile-menu-btn:hover .menu-icon span {
    background: #00f5ff;
    box-shadow: 0 0 8px rgba(0, 245, 255, 0.5);
  }

  .mobile-menu-btn:hover .menu-text {
    color: #00f5ff;
    text-shadow: 0 0 8px rgba(0, 245, 255, 0.3);
  }

  .mobile-menu-btn.menu-open:hover {
    background: rgba(255, 69, 69, 0.25);
    border-color: rgba(255, 69, 69, 0.7);
    box-shadow: 
      0 12px 35px rgba(255, 69, 69, 0.4),
      0 0 30px rgba(255, 69, 69, 0.3);
  }

  .mobile-menu-btn.menu-open:hover .menu-icon span {
    background: #ff2d2d;
    box-shadow: 0 0 8px rgba(255, 45, 45, 0.5);
  }

  .mobile-menu-btn.menu-open:hover .menu-text {
    color: #ff2d2d;
    text-shadow: 0 0 8px rgba(255, 45, 45, 0.3);
  }

  .mobile-menu-btn:active {
    transform: translateY(0) scale(0.98);
  }

  /* 广播功能区 */
  .broadcast-section {
    margin: 20px;
    margin-bottom: 0;
    background: rgba(255, 255, 255, 0.1);
    backdrop-filter: blur(20px);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 20px;
    padding: 24px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  }

  .broadcast-header {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 16px;
  }

  .broadcast-icon {
    width: 40px;
    height: 40px;
    background: linear-gradient(135deg, #ffd93d, #ff8c00);
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    box-shadow: 0 4px 12px rgba(255, 140, 0, 0.3);
  }

  .broadcast-header h4 {
    color: white;
    margin: 0;
    font-size: 18px;
    font-weight: 600;
  }

  .broadcast-controls {
    display: flex;
    gap: 12px;
    align-items: center;
    flex-wrap: wrap;
  }

  .broadcast-input {
    flex: 1;
    min-width: 300px;
    padding: 12px 16px;
    border: 2px solid rgba(255, 255, 255, 0.2);
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.1);
    color: white;
    font-size: 14px;
    transition: all 0.3s;
    backdrop-filter: blur(10px);
  }

  .broadcast-input::placeholder {
    color: rgba(255, 255, 255, 0.6);
  }

  .broadcast-input:focus {
    outline: none;
    border-color: rgba(255, 255, 255, 0.5);
    background: rgba(255, 255, 255, 0.15);
    box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.1);
  }

  .broadcast-btn {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 20px;
    background: linear-gradient(135deg, #ff6b6b, #ee5a24);
    color: white;
    border: none;
    border-radius: 12px;
    font-weight: 600;
    font-size: 14px;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0 4px 12px rgba(238, 90, 36, 0.3);
    min-width: 120px;
    justify-content: center;
  }

  .broadcast-btn:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(238, 90, 36, 0.4);
  }

  .broadcast-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  .broadcast-btn.loading {
    pointer-events: none;
  }

  .loading-spinner {
    width: 16px;
    height: 16px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-top: 2px solid white;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  .char-count {
    color: rgba(255, 255, 255, 0.7);
    font-size: 12px;
    margin-top: 8px;
    text-align: right;
  }

  /* 主内容 */
  .main-content {
    flex: 1;
    margin: 20px;
    background: rgba(255, 255, 255, 0.05);
    backdrop-filter: blur(20px);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 20px;
    overflow: hidden;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  }

  /* 管理页面容器样式 */
  .admin-page-container {
    padding-top: 40px;
  }

  /* 响应式设计 */
  @media (max-width: 768px) {
    .admin-sidebar {
      position: fixed;
      top: 0;
      left: 0;
      height: 100vh;
      width: 300px;
      z-index: 999;
    }

    .admin-main {
      width: 100%;
    }

    .mobile-menu-btn {
      display: flex;
    }

    .broadcast-section {
      margin: 80px 16px 16px;
    }

    .broadcast-controls {
      flex-direction: column;
      align-items: stretch;
    }

    .broadcast-input {
      min-width: auto;
    }

    .main-content {
      margin: 16px;
    }

    .dashboard-title {
      font-size: 16px;
    }

    .nav-label {
      font-size: 13px;
    }

    .broadcast-header h4 {
      font-size: 16px;
    }

    .admin-page-container {
      padding-top: 100px;
    }
  }

  @media (max-width: 480px) {
    .admin-sidebar {
      width: 280px;
    }

    .broadcast-section {
      padding: 16px;
      margin: 80px 12px 12px;
    }

    .main-content {
      margin: 12px;
    }

    .user-info {
      padding: 16px;
      margin: 0 12px 16px;
    }

    .sidebar-actions {
      padding: 16px;
    }
  }

  /* 滚动条样式 */
  .nav-menu::-webkit-scrollbar {
    width: 4px;
  }

  .nav-menu::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 2px;
  }

  .nav-menu::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.3);
    border-radius: 2px;
  }

  .nav-menu::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.5);
  }

  /* 动画效果 */
  .admin-sidebar {
    animation: slideInLeft 0.3s ease-out;
  }

  @keyframes slideInLeft {
    from {
      transform: translateX(-100%);
    }
    to {
      transform: translateX(0);
    }
  }

  .main-content {
    animation: fadeIn 0.4s ease-out;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  /* 确保按钮文本可见 */
  .cs-btn .btn-text,
  .logout-btn .btn-text {
    color: white;
  }

  .cs-btn .btn-icon,
  .logout-btn .btn-icon {
    font-size: 16px;
  }
`;

export default AdminDashboard;